/**
 * Settings Tab Component
 *
 * Allows users to:
 * - Manage counter availability (mark counters as unavailable)
 */

import { useState, useEffect, useRef } from "react";
import { Button } from "../Button";
import { apiClient } from "@/api/client";
import { useNotification } from "@/contexts/NotificationContext";
import {
  getModeConfig,
  formatCounterLabel,
  generateTimeSlots,
  isValidTimeRange,
} from "@/config/modeConfig";
import type { UnavailablePeriod } from "@/types/api";
import { AdminConfigPanel } from "@/components/admin/AdminConfigPanel";
import { CounterPriorityOrderSection } from "@/components/CounterPriorityOrderSection";
import { SettingsScopeSections } from "@/components/SettingsScopeSections";
import type { DutyUse } from "@/utils/dutyOptions";

interface SettingsTabProps {
  generated: boolean;
  operation_mode?: string;
  shift_type?: string;
  onUpdated?: () => void;
  isAdmin?: boolean;
  /** Who is on each non-counter deployment (see utils/dutyOptions: dutyUsage) — for the remove confirmation. */
  dutyUse?: Record<string, DutyUse>;
}

interface ZoneGroup {
  label: string;
  counters: number[];
}

interface UnavailRow {
  id: string;
  index: number | null;
  startTime: string;
  endTime: string;
  dirty: boolean;
}

interface CounterGroup {
  counter: number;
  rows: UnavailRow[];
}

function buildZoneGroups(operation_mode: string | undefined): ZoneGroup[] {
  const { zones, numCounters } = getModeConfig(operation_mode);
  const groups: ZoneGroup[] = zones.map((z, i) => ({
    label: `Zone ${i + 1}`,
    counters: Array.from({ length: z[1] - z[0] }, (_, k) => z[0] + k + 1),
  }));
  const lastZoneEnd = zones[zones.length - 1][1];
  if (lastZoneEnd < numCounters) {
    groups.push({
      label: "Motor",
      counters: Array.from(
        { length: numCounters - lastZoneEnd },
        (_, k) => lastZoneEnd + k + 1,
      ),
    });
  }
  return groups;
}

let rowIdCounter = 0;
function newRowId() {
  return `row-${++rowIdCounter}`;
}

export function SettingsTab({
  generated,
  operation_mode,
  shift_type,
  onUpdated,
  isAdmin,
  dutyUse,
}: SettingsTabProps) {
  const { notifySuccess, notifyError } = useNotification();
  const { numCounters: maxCounter } = getModeConfig(operation_mode);

  const [counterGroups, setCounterGroups] = useState<CounterGroup[]>([]);
  const [selectedCounters, setSelectedCounters] = useState<Set<number>>(
    new Set(),
  );

  const timeSlots = generateTimeSlots(shift_type ?? "morning", 1);
  const [newStartTime, setNewStartTime] = useState(
    () => generateTimeSlots(shift_type ?? "morning", 1)[0],
  );
  const [newEndTime, setNewEndTime] = useState(() => {
    const s = generateTimeSlots(shift_type ?? "morning", 1);
    return s[s.length - 1] ?? "2200";
  });
  const [availLoading, setAvailLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const zoneGroups = buildZoneGroups(operation_mode);
  const zoneCheckboxRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    zoneGroups.forEach((group, i) => {
      const ref = zoneCheckboxRefs.current[i];
      if (!ref) return;
      const sel = group.counters.filter((c) => selectedCounters.has(c)).length;
      ref.checked = sel === group.counters.length;
      ref.indeterminate = sel > 0 && sel < group.counters.length;
    });
  });

  function buildGroups(periods: UnavailablePeriod[]): CounterGroup[] {
    const map = new Map<number, UnavailRow[]>();
    periods.forEach((p, idx) => {
      if (!map.has(p.counter)) map.set(p.counter, []);
      map.get(p.counter)!.push({
        id: newRowId(),
        index: idx,
        startTime: p.start_time,
        endTime: p.end_time,
        dirty: false,
      });
    });
    return Array.from(map.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([counter, rows]) => ({ counter, rows }));
  }

  useEffect(() => {
    if (generated) loadUnavailability();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generated]);

  const loadUnavailability = async () => {
    try {
      const periods = await apiClient.getUnavailability();
      setCounterGroups(buildGroups(periods));
    } catch (err) {
      console.error("Failed to load unavailability:", err);
    }
  };

  if (!generated) {
    return (
      <div>
        <div
          className="ui-zoom"
          style={{
            background: "white",
            borderRadius: "8px",
            padding: "30px",
            textAlign: "center",
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          }}
        >
          <h2>Generate a schedule first</h2>
          <p style={{ color: "#666" }}>
            You need to generate a roster before accessing settings
          </p>
        </div>
        <SettingsScopeSections dutyUse={dutyUse} />
        {isAdmin && (
          <AdminConfigPanel
            operationMode={operation_mode}
            shiftType={shift_type}
            generated={generated}
            onApplied={onUpdated}
          />
        )}
      </div>
    );
  }

  // ── Counter multi-select helpers ─────────────────────────────────────────

  const toggleCounter = (counter: number) => {
    setSelectedCounters((prev) => {
      const next = new Set(prev);
      next.has(counter) ? next.delete(counter) : next.add(counter);
      return next;
    });
  };

  const toggleZone = (group: ZoneGroup, checked: boolean) => {
    setSelectedCounters((prev) => {
      const next = new Set(prev);
      checked
        ? group.counters.forEach((c) => next.add(c))
        : group.counters.forEach((c) => next.delete(c));
      return next;
    });
  };

  const selectAllCounters = () =>
    setSelectedCounters(
      new Set(Array.from({ length: maxCounter }, (_, i) => i + 1)),
    );
  const clearAllCounters = () => setSelectedCounters(new Set());

  // ── Modal open / close ────────────────────────────────────────────────────

  const handleOpenModal = () => {
    setSelectedCounters(new Set());
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedCounters(new Set());
  };

  // ── Add unavailability ────────────────────────────────────────────────────

  const handleAddUnavailability = async () => {
    if (selectedCounters.size === 0) {
      notifyError("Please select at least one counter.");
      return;
    }
    setAvailLoading(true);
    try {
      const result = await apiClient.addUnavailabilityBatch({
        counters: Array.from(selectedCounters),
        start_time: newStartTime,
        end_time: newEndTime,
      });
      await loadUnavailability();
      notifySuccess(result.message);
      handleCloseModal();
      onUpdated?.();
    } catch (err) {
      notifyError((err as Error)?.message ?? "Failed to add unavailability");
    } finally {
      setAvailLoading(false);
    }
  };

  // ── Grouped table: inline editing ─────────────────────────────────────────

  const updateRowField = (
    counterVal: number,
    rowId: string,
    field: "startTime" | "endTime",
    value: string,
  ) => {
    setCounterGroups((prev) =>
      prev.map((g) =>
        g.counter !== counterVal
          ? g
          : {
              ...g,
              rows: g.rows.map((r) =>
                r.id !== rowId ? r : { ...r, [field]: value, dirty: true },
              ),
            },
      ),
    );
  };

  const saveRow = async (counterVal: number, row: UnavailRow) => {
    if (
      !isValidTimeRange(row.startTime, row.endTime, shift_type ?? "morning")
    ) {
      notifyError("End time must be after start time.");
      return;
    }
    try {
      if (row.index !== null) {
        await apiClient.removeUnavailability({ index: row.index });
      }
      await apiClient.addUnavailabilityBatch({
        counters: [counterVal],
        start_time: row.startTime,
        end_time: row.endTime,
      });
      await loadUnavailability();
      notifySuccess("Updated.");
      onUpdated?.();
    } catch (err) {
      notifyError((err as Error)?.message ?? "Failed to save");
    }
  };

  const removeRow = async (row: UnavailRow) => {
    if (row.index === null) {
      setCounterGroups((prev) =>
        prev
          .map((g) => ({ ...g, rows: g.rows.filter((r) => r.id !== row.id) }))
          .filter((g) => g.rows.length > 0),
      );
      return;
    }
    try {
      await apiClient.removeUnavailability({ index: row.index });
      await loadUnavailability();
      onUpdated?.();
    } catch {
      notifyError("Failed to remove");
    }
  };

  const addSubRow = (counterVal: number) => {
    setCounterGroups((prev) =>
      prev.map((g) =>
        g.counter !== counterVal
          ? g
          : {
              ...g,
              rows: [
                ...g.rows,
                {
                  id: newRowId(),
                  index: null,
                  startTime: timeSlots[0],
                  endTime: timeSlots[timeSlots.length - 1],
                  dirty: true,
                },
              ],
            },
      ),
    );
  };

  const timeRangeValid = isValidTimeRange(
    newStartTime,
    newEndTime,
    shift_type ?? "morning",
  );

  const cellStyle: React.CSSProperties = {
    padding: "6px 8px",
    fontSize: "13px",
    verticalAlign: "middle" as const,
  };

  const selectStyle: React.CSSProperties = {
    padding: "3px 4px",
    borderRadius: "4px",
    border: "1px solid #ddd",
    fontSize: "13px",
    width: "100%",
  };

  return (
    <div
      className="ui-zoom"
      style={{
        background: "white",
        borderRadius: "8px",
        padding: "12px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
      }}
    >
      <h2 style={{ marginTop: 0, marginBottom: "8px" }}>⚙️ Settings</h2>

      <div>
        <p style={{ fontSize: "14px", color: "#666", marginBottom: "16px" }}>
          Officers will not be assigned to counters marked unavailable.
        </p>

        {/* ── Row: section label + Add button ──────────────────────────── */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "12px",
          }}
        >
          <h4 style={{ margin: 0, fontSize: "14px" }}>
            {counterGroups.length > 0 ? "Currently unavailable" : ""}
          </h4>
          <button
            onClick={handleOpenModal}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "5px",
              padding: "6px 14px",
              background: "#667eea",
              color: "white",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              fontSize: "13px",
              fontWeight: 500,
            }}
          >
            ➕ Add unavailable counter
          </button>
        </div>

        {/* ── Grouped unavailability table ───────────────────────────────── */}
        {counterGroups.length > 0 ? (
          <div style={{ marginBottom: "24px" }}>
            <div
              style={{
                border: "1px solid #e0e0e0",
                borderRadius: "8px",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "80px 1fr 1fr 80px",
                  background: "#f1f5f9",
                  borderBottom: "1px solid #e0e0e0",
                  fontWeight: 600,
                  fontSize: "14px",
                  color: "#555",
                }}
              >
                <div style={cellStyle}>Ctr no.</div>
                <div style={cellStyle}>Start time</div>
                <div style={cellStyle}>End time</div>
                <div style={cellStyle} />
              </div>

              {counterGroups.map((group, gi) => (
                <div
                  key={group.counter}
                  style={{
                    borderBottom:
                      gi < counterGroups.length - 1
                        ? "2px solid #e8edf2"
                        : "none",
                  }}
                >
                  {group.rows.map((row, ri) => {
                    const rowValid = isValidTimeRange(
                      row.startTime,
                      row.endTime,
                      shift_type ?? "morning",
                    );
                    return (
                      <div
                        key={row.id}
                        style={{
                          display: "grid",
                          gridTemplateColumns: "80px 1fr 1fr 80px",
                          background: row.dirty
                            ? "#fffbeb"
                            : ri % 2 === 0
                              ? "white"
                              : "#fafafa",
                          borderBottom:
                            ri < group.rows.length - 1
                              ? "1px solid #f0f0f0"
                              : "none",
                          alignItems: "center",
                        }}
                      >
                        <div
                          style={{
                            ...cellStyle,
                            fontWeight: 600,
                            color: "#374151",
                          }}
                        >
                          {ri === 0
                            ? formatCounterLabel(group.counter, operation_mode)
                            : ""}
                        </div>

                        <div style={cellStyle}>
                          <select
                            value={row.startTime}
                            onChange={(e) =>
                              updateRowField(
                                group.counter,
                                row.id,
                                "startTime",
                                e.target.value,
                              )
                            }
                            style={{
                              ...selectStyle,
                              borderColor: rowValid ? "#ddd" : "#dc3545",
                            }}
                          >
                            {timeSlots.map((s) => (
                              <option key={s} value={s}>
                                {s}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div style={cellStyle}>
                          <select
                            value={row.endTime}
                            onChange={(e) =>
                              updateRowField(
                                group.counter,
                                row.id,
                                "endTime",
                                e.target.value,
                              )
                            }
                            style={{
                              ...selectStyle,
                              borderColor: rowValid ? "#ddd" : "#dc3545",
                            }}
                          >
                            {timeSlots.map((s) => (
                              <option key={s} value={s}>
                                {s}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div
                          style={{
                            ...cellStyle,
                            display: "flex",
                            gap: "4px",
                            justifyContent: "flex-end",
                          }}
                        >
                          {row.dirty && (
                            <button
                              onClick={() => saveRow(group.counter, row)}
                              disabled={!rowValid}
                              title="Save changes"
                              style={{
                                padding: "3px 7px",
                                background: rowValid ? "#22c55e" : "#d1d5db",
                                color: "white",
                                border: "none",
                                borderRadius: "4px",
                                cursor: rowValid ? "pointer" : "not-allowed",
                                fontSize: "12px",
                              }}
                            >
                              ✓
                            </button>
                          )}
                          <button
                            onClick={() => removeRow(row)}
                            title="Remove"
                            style={{
                              padding: "3px 7px",
                              background: "#dc3545",
                              color: "white",
                              border: "none",
                              borderRadius: "4px",
                              cursor: "pointer",
                              fontSize: "12px",
                            }}
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                    );
                  })}

                  <div
                    style={{
                      padding: "4px 8px",
                      background: "#fafafa",
                      borderTop: "1px dashed #e8edf2",
                    }}
                  >
                    <button
                      onClick={() => addSubRow(group.counter)}
                      style={{
                        fontSize: "11px",
                        color: "#667eea",
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        padding: "2px 0",
                      }}
                    >
                      + add time range
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div
            style={{
              padding: "8px",
              background: "#d4edda",
              color: "#155724",
              borderRadius: "6px",
              marginBottom: "24px",
            }}
          >
            ✅ All counters are currently available.
          </div>
        )}
      </div>

      {/* ── Priority counters ────────────────────────────────────────────── */}
      <div style={{ borderTop: "1px solid #e5e7eb", paddingTop: 16, marginBottom: 16 }}>
        <h4 style={{ margin: "0 0 8px", fontSize: "19px", fontWeight: 700, letterSpacing: "-0.01em", color: "#0f172a" }}>
          Priority Counters
        </h4>
        <CounterPriorityOrderSection operation_mode={operation_mode} shift_type={shift_type} />
      </div>

      <SettingsScopeSections dutyUse={dutyUse} />

      {/* ── Modal overlay ─────────────────────────────────────────────────── */}
      {showModal && (
        <div
          onClick={handleCloseModal}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0, 0, 0, 0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 4600,
            padding: "16px",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "white",
              borderRadius: "10px",
              padding: "20px",
              width: "100%",
              maxWidth: "480px",
              maxHeight: "calc(90vh / var(--ui-scale, 1))",
              overflowY: "auto",
              boxShadow: "0 4px 24px rgba(0,0,0,0.18)",
            }}
          >
            {/* Modal header */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "16px",
              }}
            >
              <h3 style={{ margin: 0, fontSize: "14px" }}>
                Add Unavailable Counter
              </h3>
              <button
                onClick={handleCloseModal}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "18px",
                  cursor: "pointer",
                  color: "#666",
                  lineHeight: 1,
                  padding: "0 2px",
                }}
              >
                ✕
              </button>
            </div>

            {/* Counter multi-select */}
            <div style={{ marginBottom: "16px" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: "8px",
                }}
              >
                <label style={{ fontWeight: 600 }}>
                  Counters
                  {selectedCounters.size > 0 && (
                    <span
                      style={{
                        marginLeft: "6px",
                        fontSize: "14px",
                        color: "#667eea",
                        fontWeight: 400,
                      }}
                    >
                      ({selectedCounters.size} selected)
                    </span>
                  )}
                </label>
                <div style={{ display: "flex", gap: "6px" }}>
                  <button
                    onClick={selectAllCounters}
                    style={{
                      fontSize: "14px",
                      padding: "2px 8px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      cursor: "pointer",
                      background: "#f8f9fa",
                    }}
                  >
                    All
                  </button>
                  <button
                    onClick={clearAllCounters}
                    style={{
                      fontSize: "14px",
                      padding: "2px 8px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      cursor: "pointer",
                      background: "#f8f9fa",
                    }}
                  >
                    None
                  </button>
                </div>
              </div>

              <div
                style={{
                  border: "1px solid #ddd",
                  borderRadius: "8px",
                  padding: "8px",
                }}
              >
                {zoneGroups.map((group, gi) => (
                  <div key={group.label} style={{ marginBottom: "8px" }}>
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        fontWeight: 600,
                        fontSize: "14px",
                        color: "#555",
                        cursor: "pointer",
                        userSelect: "none",
                        marginBottom: "4px",
                      }}
                    >
                      <input
                        type="checkbox"
                        ref={(el) => {
                          zoneCheckboxRefs.current[gi] = el;
                        }}
                        checked={
                          group.counters.filter((c) => selectedCounters.has(c))
                            .length === group.counters.length
                        }
                        onChange={(e) => toggleZone(group, e.target.checked)}
                        style={{
                          width: "14px",
                          height: "14px",
                          cursor: "pointer",
                        }}
                      />
                      {group.label} ({group.counters.length} counters)
                    </label>

                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "4px",
                        paddingLeft: "20px",
                      }}
                    >
                      {group.counters.map((counter) => (
                        <label
                          key={counter}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "3px",
                            fontSize: "14px",
                            padding: "2px 6px",
                            borderRadius: "4px",
                            border: `1px solid ${selectedCounters.has(counter) ? "#667eea" : "#e0e0e0"}`,
                            background: selectedCounters.has(counter)
                              ? "#ede9fe"
                              : "#f9f9f9",
                            cursor: "pointer",
                            userSelect: "none",
                            minWidth: "60px",
                            flex: "1 1 60px",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={selectedCounters.has(counter)}
                            onChange={() => toggleCounter(counter)}
                            style={{
                              width: "11px",
                              height: "11px",
                              cursor: "pointer",
                            }}
                          />
                          {formatCounterLabel(counter, operation_mode)}
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Time range selects */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                gap: "16px",
                marginBottom: "16px",
              }}
            >
              <div>
                <label
                  style={{
                    display: "block",
                    fontWeight: 600,
                    marginBottom: "8px",
                    fontSize: "14px",
                  }}
                >
                  Unavailable from
                </label>
                <select
                  value={newStartTime}
                  onChange={(e) => setNewStartTime(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "5px",
                    borderRadius: "6px",
                    border: timeRangeValid
                      ? "1px solid #ddd"
                      : "1px solid #dc3545",
                    fontSize: "14px",
                    color: timeRangeValid ? undefined : "#dc3545",
                  }}
                >
                  {timeSlots.map((slot) => (
                    <option key={slot} value={slot}>
                      {slot}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label
                  style={{
                    display: "block",
                    fontWeight: 600,
                    marginBottom: "8px",
                    fontSize: "14px",
                  }}
                >
                  To
                </label>
                <select
                  value={newEndTime}
                  onChange={(e) => setNewEndTime(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "5px",
                    borderRadius: "6px",
                    border: timeRangeValid
                      ? "1px solid #ddd"
                      : "1px solid #dc3545",
                    fontSize: "14px",
                    color: timeRangeValid ? undefined : "#dc3545",
                  }}
                >
                  {timeSlots.map((slot) => (
                    <option key={slot} value={slot}>
                      {slot}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {!timeRangeValid && (
              <p
                style={{
                  color: "#dc3545",
                  fontSize: "14px",
                  marginBottom: "8px",
                }}
              >
                End time must be after start time.
              </p>
            )}

            {/* Modal footer buttons */}
            <div
              style={{
                display: "flex",
                gap: "8px",
                justifyContent: "flex-end",
                marginTop: "4px",
              }}
            >
              <button
                onClick={handleCloseModal}
                style={{
                  padding: "7px 16px",
                  background: "#f8f9fa",
                  border: "1px solid #ddd",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "14px",
                }}
              >
                Cancel
              </button>
              <Button
                onClick={handleAddUnavailability}
                disabled={availLoading || !timeRangeValid}
              >
                {availLoading ? "⏳ Adding..." : "✅ Confirm"}
              </Button>
            </div>
          </div>
        </div>
      )}
      {isAdmin && (
        <AdminConfigPanel
          operationMode={operation_mode}
          shiftType={shift_type}
          generated={generated}
          onApplied={onUpdated}
        />
      )}
    </div>
  );
}
